import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { extractError } from '../../api/client';
import { shareKnowledgeApi } from '../../api/shareKnowledge';

const fetchItems = createAsyncThunk('shareKnowledge/fetchItems', async (params = {}, { rejectWithValue }) => {
  try {
    return await shareKnowledgeApi.adminList(params);
  } catch (error) {
    return rejectWithValue(extractError(error));
  }
});

const updateItem = createAsyncThunk(
  'shareKnowledge/updateItem',
  async ({ id, payload, onUploadProgress }, { rejectWithValue }) => {
    try {
      return await shareKnowledgeApi.adminUpdate(id, payload, { onUploadProgress });
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  }
);

const createItem = createAsyncThunk(
  'shareKnowledge/createItem',
  async ({ payload, onUploadProgress }, { rejectWithValue }) => {
    try {
      return await shareKnowledgeApi.adminCreate(payload, { onUploadProgress });
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  }
);

const deleteItem = createAsyncThunk('shareKnowledge/deleteItem', async (id, { rejectWithValue }) => {
  try {
    await shareKnowledgeApi.adminRemove(id);
    return id;
  } catch (error) {
    return rejectWithValue(extractError(error));
  }
});

const approveItem = createAsyncThunk('shareKnowledge/approveItem', async (id, { rejectWithValue }) => {
  try {
    return await shareKnowledgeApi.approve(id);
  } catch (error) {
    return rejectWithValue(extractError(error));
  }
});

const rejectItem = createAsyncThunk('shareKnowledge/rejectItem', async (id, { rejectWithValue }) => {
  try {
    return await shareKnowledgeApi.reject(id);
  } catch (error) {
    return rejectWithValue(extractError(error));
  }
});

const publishItem = createAsyncThunk('shareKnowledge/publishItem', async (id, { rejectWithValue }) => {
  try {
    return await shareKnowledgeApi.publishToSite(id);
  } catch (error) {
    return rejectWithValue(extractError(error));
  }
});

const replaceItem = (state, payload) => {
  state.items = state.items.map((it) => (it.id === payload.id ? { ...it, ...payload } : it));
};

const shareKnowledgeSlice = createSlice({
  name: 'shareKnowledge',
  initialState: {
    items: [],
    count: 0,
    status: 'idle',
    error: null,
    submitting: false,
  },
  reducers: {
    clearContentError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchItems.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchItems.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = action.payload.results ?? action.payload;
        state.count = action.payload.count ?? state.items.length;
      })
      .addCase(fetchItems.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload?.message || 'Failed to load shared knowledge';
      })
      .addCase(updateItem.pending, (state) => {
        state.submitting = true;
      })
      .addCase(updateItem.fulfilled, (state, action) => {
        state.submitting = false;
        replaceItem(state, action.payload);
      })
      .addCase(updateItem.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload?.message || 'Failed to update entry';
      })
      .addCase(createItem.pending, (state) => {
        state.submitting = true;
      })
      .addCase(createItem.fulfilled, (state, action) => {
        state.submitting = false;
        state.items = [action.payload, ...state.items];
        state.count += 1;
      })
      .addCase(createItem.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload?.message || 'Failed to create entry';
      })
      .addCase(deleteItem.pending, (state) => {
        state.submitting = true;
      })
      .addCase(deleteItem.fulfilled, (state, action) => {
        state.submitting = false;
        state.items = state.items.filter((it) => it.id !== action.payload);
        state.count = Math.max(0, state.count - 1);
      })
      .addCase(deleteItem.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload?.message || 'Failed to delete entry';
      })
      .addCase(approveItem.fulfilled, (state, action) => {
        replaceItem(state, action.payload);
      })
      .addCase(approveItem.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to approve entry';
      })
      .addCase(rejectItem.fulfilled, (state, action) => {
        replaceItem(state, action.payload);
      })
      .addCase(rejectItem.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to reject entry';
      })
      .addCase(publishItem.fulfilled, (state, action) => {
        replaceItem(state, action.payload);
      })
      .addCase(publishItem.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to publish entry';
      });
  },
});

export const shareKnowledgeReducer = shareKnowledgeSlice.reducer;
export const { clearContentError } = shareKnowledgeSlice.actions;

export const shareKnowledgeActions = {
  ...shareKnowledgeSlice.actions,
  fetchItems,
  createItem,
  updateItem,
  deleteItem,
  approveItem,
  rejectItem,
  publishItem,
};

export const selectShareKnowledge = (state) => state.shareKnowledge.items;
export const selectShareKnowledgeStatus = (state) => state.shareKnowledge.status;